import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import MemoryGame from './components/MemoryGame';
import Leaderboard, { Score } from './components/Leaderboard';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { getScores, saveScore } from './services/api';
import { v4 as uuidv4 } from 'uuid';
import Container from '@mui/material/Container';
import { getPlayer, savePlayer } from './services/localStorage';

const theme = createTheme({
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
  },
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

// Define Player type
interface Player {
  email: string;
  name: string;
  phone?: string;
  interest?: string;
  company: string;
  id: string;
}

const App = () => {
  const [scores, setScores] = useState<Score[]>([]);
  const [player, setPlayer] = useState<Player | null>(null);
  const [open, setOpen] = useState(true);
  const [form, setForm] = useState<Player>({
    email: '',
    name: '',
    phone: '',
    interest: '',
    company: '',
    id: '',
  });
  const [formError, setFormError] = useState({ email: false, name: false });

  useEffect(() => {
    // Load player from localStorage
    const loadPlayer = () => {
      try {
        const storedPlayer = getPlayer();
        if (storedPlayer) {
          setPlayer(storedPlayer);
          setOpen(false);
        }
      } catch (error) {
        console.error('Error loading player from localStorage:', error);
      }
    };

    // Load scores from API
    const loadScores = async () => {
      try {
        const fetchedScores = await getScores();
        setScores(
          fetchedScores.map((s: any) => ({
            moves: s.moves,
            player: {
              name: s.playerName,
              email: s.email || '',
              phone: s.phone,
              interest: s.interest,
              company: s.company || '',
              id: s.id,
            },
          }))
        );
      } catch (error) {
        console.error('Failed to load scores:', error);
      }
    };

    loadPlayer();
    loadScores();
  }, []);

  const handleFormChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const emailValid = form.email.trim() !== '' && /.+@.+\..+/.test(form.email);
    const nameValid = form.name.trim() !== '';
    setFormError({ email: !emailValid, name: !nameValid });
    
    if (emailValid && nameValid) {
      const playerWithId = { ...form, id: uuidv4() };
      try {
        savePlayer(playerWithId);
        setPlayer(playerWithId);
        setOpen(false);
      } catch (error) {
        console.error('Error saving player to localStorage:', error);
        // If localStorage fails, still set the player but don't persist
        setPlayer(playerWithId);
        setOpen(false);
      }
    }
  };

  const handleScoreSaved = async (score: { moves: number; player: Player }) => {
    try {
      const savedScore = await saveScore({
        id: player?.id || uuidv4(),
        playerName: player?.name || score.player.name,
        email: player?.email || '',
        phone: player?.phone,
        interest: player?.interest,
        company: player?.company,
        moves: score.moves,
        timestamp: new Date().toISOString(),
      });
      setScores((prevScores) => [
        ...prevScores,
        {
          moves: savedScore.moves,
          player: {
            name: savedScore.playerName,
            email: savedScore.email || '',
            company: savedScore.company || '',
            id: savedScore.id,
          },
        },
      ]);
    } catch (error) {
      console.error('Failed to save score:', error);
    }
  };

  // Prevent dialog close
  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      return;
    }
    setOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Dialog open={open} onClose={handleDialogClose}>
        <DialogTitle>Enter your details to play</DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Email"
              name="email"
              type="email"
              fullWidth
              required
              value={form.email}
              onChange={handleFormChange}
              error={formError.email}
              helperText={formError.email ? 'Valid email required' : ''}
            />
            <TextField
              margin="dense"
              label="Full Name"
              name="name"
              fullWidth
              required
              value={form.name}
              onChange={handleFormChange}
              error={formError.name}
              helperText={formError.name ? 'Name required' : ''}
            />
            <TextField
              margin="dense"
              label="Phone"
              name="phone"
              fullWidth
              value={form.phone}
              onChange={handleFormChange}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Business/Personal Interest</InputLabel>
              <Select
                name="interest"
                value={form.interest}
                onChange={handleFormChange}
                label="Business/Personal Interest"
              >
                <MenuItem value="Business">Business</MenuItem>
                <MenuItem value="Personal">Personal</MenuItem>
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              label="Company (Optional)"
              name="company"
              fullWidth
              value={form.company}
              onChange={handleFormChange}
            />
          </DialogContent>
          <DialogActions>
            <Button type="submit" variant="contained">Start Game</Button>
          </DialogActions>
        </form>
      </Dialog>
      <Container maxWidth="md">
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <img
              src="https://myops.co.il/img/logo.png"
              alt="MyOps Logo"
              style={{ maxWidth: 180, height: 'auto', marginBottom: 16 }}
            />
          </Box>
          {player && <MemoryGame player={player} onScoreSaved={handleScoreSaved} />}
          <Box sx={{ mt: 4 }}>
            <Leaderboard scores={scores} />
          </Box>
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default App; 